import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import { observer } from 'mobx-react';
import moment from 'moment';
import * as React from 'react';
import { Waypoint } from 'react-waypoint';
import { IAutomationInfo, SizeConstraint } from '../../../../models';
import { useActionItemComposer, useErrorMessages, useNoteComposer } from '../../../../models/hooks/appStateHooks';
import { ILocalNotification } from '../../../../models/LocalNotifications';
import { Topics } from '../../../../models/LocalNotificationTopics';
import { useEventLogging } from '../../../../models/Logging';
import {
	convertRawRichTextContentStateToRichContentEditorState,
	createContentStateWithHtmlStringValue,
} from '../../../../models/UiUtils';
import { Dropdown, DropdownContent, DropdownEllipsisTrigger, DropdownItem } from '../../../components-v2/Dropdown';
import { bs } from '../../../styles/styles';
import { ActionItemFeedCard } from '../../cards/ActionItemFeedCard';
import { ConfirmationDialog, IConfirmationDialogOption } from '../../ConfirmationDialog';
import { LoadingSpinner } from '../../LoadingSpinner';
import { LocalNotificationObserver } from '../../LocalNotificationObserver';
import { DefaultCompactEditorConfig } from '../../notes/NoteEditor';
import { Placeholder } from '../../Placeholder';
import { RichContentDocumentEditor } from '../../richContent/RichContentDocumentEditor';
import { NotesPlaceholderIcon } from '../../svgs/icons/NotesPlaceholderIcon';
import { WarningIcon } from '../../svgs/icons/WarningIcon';
import { TimelineScheduledMeeting } from '../../TimelineScheduledMeeting';
import { EntityConversationContent } from '../EntityConversationContent';
import { EntityModalContent } from '../EntityModalContent';
import { useCompanyTimelineData } from './hooks/useTimelineData';
import {
	EntityTimelineEventAction,
	EntityTimelineEventDateHeader,
	EntityTimelineEventIcon,
	EntityTimelineEventTitle,
	EntityTimelineNoteEventContent,
	EntityTimelineSatisfactionSurveyResponseEventContent,
} from './presentation';
import { styleSheet } from './styles';

interface IProps {
	className?: string;
	contact?: Api.IContact;
	onEntityClicked?(entity: Api.EntityViewModel<Api.IEntity>, e: React.MouseEvent<HTMLElement>): void;
	onMoreMenuOptionClicked?(
		richContent: Api.RichContentViewModel,
		option: 'edit' | 'delete',
		e: React.MouseEvent<HTMLElement>
	): void;
	readonly?: boolean;
	renderAction?(evt: Api.TimelineEventViewModel): JSX.Element;
	renderActionFor?: Api.TimelineEventTypes[];
	renderContent?(evt: Api.TimelineEventViewModel): JSX.Element;
	renderContentFor?: Api.TimelineEventTypes[];
	placeholderText?: string;
	sendMessageDisabled?: boolean;
	sizeConstraint?: SizeConstraint;
	onAutomationEdited: (notification?: ILocalNotification<IAutomationInfo>) => void;
	onCreateOrUpdateNotificationReceived: (notification: ILocalNotification<Api.IActionItem>) => void;
	onEmailsSent?: (notification: ILocalNotification<Api.EmailMessageViewModel>) => void;
	onNoteCreateOrUpdateNotificationReceived: (notification: ILocalNotification<Api.INote>) => void;
	onTimelineRefreshNotificationReceived: () => void;
	timelineData: ReturnType<typeof useCompanyTimelineData>;
	onDeleteRichContent: (richContent: Api.RichContentViewModel) => Promise<Api.IRichContent>;
	onTimelineDataUpdated: () => void;
}

function _Timeline({
	contact,
	className,
	sizeConstraint,
	placeholderText,
	renderAction,
	renderActionFor,
	readonly,
	renderContentFor,
	renderContent: renderContentProp,
	timelineData,
	onAutomationEdited,
	onCreateOrUpdateNotificationReceived,
	onEmailsSent,
	onNoteCreateOrUpdateNotificationReceived,
	onTimelineRefreshNotificationReceived,
	onTimelineDataUpdated,
	onDeleteRichContent,
}: IProps) {
	const errorMessages = useErrorMessages();
	const { logInput, logApiError } = useEventLogging('Timeline');
	const [showDeletingRichContentModal, setShowDeletingRichContentModal] =
		React.useState<Api.RichContentViewModel>(null);
	const noteComposer = useNoteComposer();
	const actionItemComposer = useActionItemComposer();
	const onDeleteContentConfirmationDialogRequestClose = async (
		result?: IConfirmationDialogOption<boolean>,
		canceled?: boolean
	) => {
		if (!!result && !canceled && !!showDeletingRichContentModal) {
			logInput('MoreMenuDeleteNote', 'Click', {
				id: showDeletingRichContentModal.id,
			});
			try {
				await onDeleteRichContent(showDeletingRichContentModal);
				onTimelineDataUpdated();
			} catch (error) {
				logApiError('NoteDelete-Error', error);
				errorMessages.pushApiError(error);
			}
		}
		setShowDeletingRichContentModal(null);
	};
	const renderContent = (event: Api.TimelineEventViewModel) => {
		switch (event.type) {
			case 'ReceivedEmailEvent':
			case 'ViewedEmailEvent':
			case 'RepliedEmailEvent':
			case 'HtmlNewsletterEvent':
			case 'SentEmailEvent': {
				const evt = event as Api.SentEmailEventViewModel;
				if (evt.viewmodel?.rawContentState) {
					return <EntityModalContent event={evt} />;
				}
				return null;
			}
			case 'DealCreatedEvent':
			case 'DealUpdatedEvent': {
				const noteEvt = event as Api.NoteEventViewModel;
				if (noteEvt.viewmodel.rawContentState) {
					return (
						<>
							<RichContentDocumentEditor
								autoFocus={false}
								className='note-editor-body-editor'
								config={DefaultCompactEditorConfig}
								contentState={convertRawRichTextContentStateToRichContentEditorState(noteEvt.viewmodel.rawContentState)}
								readOnly={true}
							/>

							<TimelineScheduledMeeting scheduledMeeting={event?.scheduledMeeting} />
						</>
					);
				}

				return <TimelineScheduledMeeting scheduledMeeting={event?.scheduledMeeting} />;
			}
			case 'NoteEvent': {
				const note = event as Api.NoteEventViewModel;
				if (note.viewmodel.rawContentState) {
					return <EntityTimelineNoteEventContent note={note} />;
				}
				return null;
			}
			case 'PhoneCallCompletedEvent':
			case 'PhoneCallEvent':
			case 'UntrackedPhoneCallEvent':
			case 'SkipLeadEvent': {
				const note = event as Api.NoteEventViewModel;
				if (note.viewmodel.rawContentState) {
					return (
						<RichContentDocumentEditor
							autoFocus={false}
							className='note-editor-body-editor'
							config={DefaultCompactEditorConfig}
							contentState={convertRawRichTextContentStateToRichContentEditorState(note.viewmodel.rawContentState)}
							readOnly={true}
						/>
					);
				}
				return null;
			}
			case 'ActionItemEvent': {
				const vm = (event as Api.ActionItemEventViewModel).viewmodel;
				return <ActionItemFeedCard actionItem={vm} moreMenuDisabled={true} />;
			}
			case 'ConversationThreadEvent': {
				const conversation = event as Api.ConversationThreadEventViewModel;
				return <EntityConversationContent event={conversation} entity={contact} />;
			}
			case 'SatisfactionSurveyResponseEvent': {
				const satisfactionSurveyResponseEvent = event as Api.SatisfactionSurveyResponseEventViewModel;
				return <EntityTimelineSatisfactionSurveyResponseEventContent event={satisfactionSurveyResponseEvent} />;
			}
			case 'HandwrittenCardOrderEvent': {
				const cardOrder = event as Api.HandwrittenCardOrderEventViewModel;
				const signature = cardOrder.cardSignature ? `<p>${cardOrder.cardSignature}</p>` : '';
				const content = `<p>${cardOrder.cardContent}</p>${signature}`;
				return (
					<RichContentDocumentEditor
						autoFocus={false}
						className='note-editor-body-editor'
						config={DefaultCompactEditorConfig}
						contentState={createContentStateWithHtmlStringValue(content)}
						readOnly
					/>
				);
				return null;
			}
			case 'MeetingEvent':
			case 'MeetingScheduledEvent':
			case 'ExternalSentEmailEvent':
			default: {
				return null;
			}
		}
	};
	const editClick = (richContent: Api.RichContentViewModel) => {
		if (richContent instanceof Api.NoteViewModel) {
			noteComposer.show(richContent, (editedNote?: Api.NoteViewModel, _result?: Api.RichContentComposerResult) => {
				if (!editedNote) {
					return;
				}
				onTimelineDataUpdated();
			});
		} else if (richContent instanceof Api.ActionItemViewModel) {
			actionItemComposer.show(
				richContent,
				(editedActionItem?: Api.ActionItemViewModel, _result?: Api.RichContentComposerResult) => {
					if (!editedActionItem) {
						return;
					}
					onTimelineDataUpdated();
				}
			);
		}
	};
	const renderMenu = (event: Api.TimelineEventViewModel) => {
		const richContent: Api.RichContentViewModel = (event as Api.NoteEventViewModel).viewmodel;
		if (!richContent) {
			return null;
		}
		return (
			<Dropdown>
				<DropdownEllipsisTrigger styles={[bs.ml2]} />
				<DropdownContent>
					{richContent.canEdit ? <DropdownItem onClick={() => editClick(richContent)}>Edit</DropdownItem> : null}
					{richContent.canDelete ? (
						<DropdownItem onClick={() => setShowDeletingRichContentModal(richContent)}>Delete</DropdownItem>
					) : null}
				</DropdownContent>
			</Dropdown>
		);
	};

	const renderEvent = (event: Api.TimelineEventViewModel | Date) => {
		if (!(event instanceof Api.TimelineEventViewModel)) {
			return <EntityTimelineEventDateHeader date={event} key={event.toUTCString()} />;
		}
		return (
			<div className={css(styleSheet.eventContainer)} key={event.id} data-id={event.id}>
				<div className={css(styleSheet.eventTime)}>{moment(event.timestamp).format('h:mm A')}</div>
				<div className={css(styleSheet.eventIconContainer)}>
					<div className={css(styleSheet.eventIcon)}>
						<EntityTimelineEventIcon event={event} />
					</div>
					<div className={css(styleSheet.eventFillLine)} />
				</div>
				<div className={css(styleSheet.eventBodyContainer)}>
					<div className={css(styleSheet.eventTitleContainer)}>
						<EntityTimelineEventTitle event={event} />
						<div className={css(styleSheet.eventAccessory)}>
							{!!renderAction && renderActionFor?.includes(event.type) ? (
								renderAction(event)
							) : (
								<EntityTimelineEventAction event={event} menu={!readonly ? renderMenu(event) : null} />
							)}
						</div>
					</div>
					<div className={css(styleSheet.eventContent)}>
						{!!renderContentProp && renderContentFor?.includes(event.type)
							? renderContentProp(event)
							: renderContent(event)}
					</div>
				</div>
			</div>
		);
	};

	const renderData = () => {
		if (timelineData.status === 'pending') {
			return <LoadingSpinner type='large' />;
		}
		if (timelineData.status === 'success') {
			return (
				<>
					{timelineData.data.pages[0].values.length === 0 ? (
						<Placeholder
							isEmpty={true}
							loaderType={sizeConstraint === 'compact' ? 'small' : 'large'}
							message={placeholderText || "You haven't added any notes or reminders yet"}
							placeholderIcon={<NotesPlaceholderIcon />}
						/>
					) : null}
					{timelineData.data.pages.map(group =>
						group.values
							.filter(event => {
								// Sometimes the messages in conversation thread events are empty.
								// If an events last messages are all empty, skip the event in the timeline.
								if (!(event instanceof Date)) {
									const data = event.toJs();
									if (data._type === 'ConversationThreadEvent') {
										const conversationThreadEvent = data as Api.IConversationThreadEvent;
										if (!conversationThreadEvent.lastMessages?.some(msg => msg.text || msg.media)) {
											return false;
										}
									}
								}
								return true;
							})
							.map(item => {
								// Extract explicit key for better React tracking
								const key = item instanceof Api.TimelineEventViewModel ? item.id : (item as Date).toUTCString();
								return <React.Fragment key={key}>{renderEvent(item)}</React.Fragment>;
							})
					)}
					{timelineData.hasNextPage && !timelineData.isFetchingNextPage ? (
						<Waypoint
							bottomOffset='-600px'
							onEnter={() => {
								timelineData.fetchNextPage();
							}}
						/>
					) : null}
					<LocalNotificationObserver
						topic={Topics.CREATE_ACTION_ITEM}
						onNotificationReceived={onCreateOrUpdateNotificationReceived}
					/>
					<LocalNotificationObserver
						topic={Topics.EDIT_ACTION_ITEM}
						onNotificationReceived={onCreateOrUpdateNotificationReceived}
					/>
					<LocalNotificationObserver
						topic={Topics.TIMELINE_REFRESH}
						onNotificationReceived={onTimelineRefreshNotificationReceived}
					/>
					<LocalNotificationObserver
						topic={Topics.CREATE_NOTE}
						onNotificationReceived={onNoteCreateOrUpdateNotificationReceived}
					/>
					<LocalNotificationObserver
						topic={Topics.EDIT_NOTE}
						onNotificationReceived={onNoteCreateOrUpdateNotificationReceived}
					/>
					{onEmailsSent ? (
						<LocalNotificationObserver topic={Topics.SEND_EMAIL} onNotificationReceived={onEmailsSent} />
					) : null}
					<LocalNotificationObserver topic={Topics.CREATE_AUTOMATION} onNotificationReceived={onAutomationEdited} />
					<LocalNotificationObserver topic={Topics.DELETE_AUTOMATION} onNotificationReceived={onAutomationEdited} />
					<ConfirmationDialog
						icon={<WarningIcon />}
						modalProps={{
							isOpen: !!showDeletingRichContentModal,
							onRequestClose: onDeleteContentConfirmationDialogRequestClose,
						}}
						options={[
							{
								isDestructive: true,
								representedObject: true,
								title: 'Delete',
							},
							{
								isCancel: true,
								representedObject: false,
								title: 'Cancel',
							},
						]}
						title={`Are you sure you want to delete this ${
							showDeletingRichContentModal?.toJs()?._type === 'ActionItem' ? 'action item' : 'note'
						}?`}
					/>
				</>
			);
		}
	};
	return <ul className={`${css(styleSheet.container)} entity-rich-content ${className || ''}`}>{renderData()}</ul>;
}

export const Timeline = observer(_Timeline);
